import { useCallback, useState, type FC } from "react";
import { ActionIcon, Box, Card, Flex, Image, Title } from "@mantine/core";
import { IconPlayerPlayFilled } from "@tabler/icons-react";
import { Event } from "../../../constants/events.js";
import { captureEvent } from "../../../utils/posthog.js";

interface LearnByWatchingCardProps {
  youtubeId: string;
  title: string;
  background: string;
}

const CARD_HEIGHT = 180;

export const LearnByWatchingCard: FC<LearnByWatchingCardProps> = ({
  youtubeId,
  title,
  background,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = useCallback(() => {
    captureEvent(Event.LearnByWatchingVideoStarted, { title });
    setIsPlaying(true);
  }, [youtubeId, title]);

  if (isPlaying) {
    return (
      <Card p={0} w="100%" h="100%" style={{ overflow: "hidden" }}>
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          style={{ border: 0, display: "block" }}
        />
      </Card>
    );
  }

  const thumbnail = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;

  return (
    <Card
      p={0}
      h={CARD_HEIGHT}
      bg={background}
      pos="relative"
      role="button"
      tabIndex={0}
      aria-label={title}
      onClick={handlePlay}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handlePlay();
        }
      }}
      style={{ cursor: "pointer", overflow: "hidden" }}
    >
      <Flex h="100%">
        <Box w={149} p={16}>
          <Title order={5}>{title}</Title>
        </Box>
        <Box flex={1}>
          <Image src={thumbnail} w="100%" h="100%" fit="cover" />
        </Box>
      </Flex>
      <ActionIcon
        component="div"
        pos="absolute"
        top="50%"
        left="50%"
        size={48}
        radius="xl"
        color="yellow"
        style={{ transform: "translate(-50%, -50%)", pointerEvents: "none" }}
      >
        <IconPlayerPlayFilled size={22} />
      </ActionIcon>
    </Card>
  );
};
